import { useCallback, useMemo, useState } from 'react';
import { StepIndicator } from './components/StepIndicator';
import { FilingStatusStep } from './steps/FilingStatus';
import { DependentsStep } from './steps/Dependents';
import { IncomeStep } from './steps/Income';
import { DeductionsStep } from './steps/Deductions';
import { ResultStep } from './steps/Result';
import type { FilingStatus } from './data/federal-2026';
import { calculateRefund } from './lib/calculate';
import type { CalcResult } from './lib/calculate';

export interface WizardState {
  filingStatus: FilingStatus;
  dependents: number;
  wages: number;
  federalWithholding: number;
  retirement401k: number;
  hsaContribution: number;
  studentLoanInterest: number;
}

export const INITIAL_STATE: WizardState = {
  filingStatus: 'single',
  dependents: 0,
  wages: 0,
  federalWithholding: 0,
  retirement401k: 0,
  hsaContribution: 0,
  studentLoanInterest: 0,
};

const STEP_DEFS = [
  { key: 'filing', label: 'Filing status' },
  { key: 'deps', label: 'Dependents' },
  { key: 'income', label: 'Income' },
  { key: 'deductions', label: 'Deductions' },
  { key: 'result', label: 'Result' },
] as const;

export function Wizard() {
  const [state, setState] = useState<WizardState>(INITIAL_STATE);
  const [step, setStep] = useState(0);

  const result: CalcResult = useMemo(
    () =>
      calculateRefund({
        filingStatus: state.filingStatus,
        dependents: state.dependents,
        wages: state.wages,
        federalWithholding: state.federalWithholding,
        retirement401k: state.retirement401k,
        hsaContribution: state.hsaContribution,
        studentLoanInterest: state.studentLoanInterest,
      }),
    [state],
  );

  const goNext = useCallback(() => {
    setStep((s) => Math.min(s + 1, STEP_DEFS.length - 1));
  }, []);
  const goPrev = useCallback(() => {
    setStep((s) => Math.max(s - 1, 0));
  }, []);
  const goTo = useCallback((idx: number) => {
    setStep(Math.max(0, Math.min(idx, STEP_DEFS.length - 1)));
  }, []);

  const restart = useCallback(() => {
    setState(INITIAL_STATE);
    setStep(0);
  }, []);

  const canGoNext = (() => {
    switch (step) {
      case 0:
        return true;
      case 1:
        return state.dependents >= 0;
      case 2:
        return state.wages >= 0 && state.federalWithholding >= 0;
      case 3:
        return (
          state.retirement401k >= 0 &&
          state.hsaContribution >= 0 &&
          state.studentLoanInterest >= 0
        );
      default:
        return false;
    }
  })();

  return (
    <div className="space-y-8">
      <StepIndicator
        steps={STEP_DEFS.map((s) => ({ key: s.key, label: s.label }))}
        current={step}
        onJump={goTo}
      />

      <div className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8">
        {step === 0 && (
          <FilingStatusStep
            value={state.filingStatus}
            onChange={(filingStatus) =>
              setState((s) => ({ ...s, filingStatus }))
            }
          />
        )}
        {step === 1 && (
          <DependentsStep
            value={state.dependents}
            onChange={(dependents) => setState((s) => ({ ...s, dependents }))}
          />
        )}
        {step === 2 && (
          <IncomeStep
            wages={state.wages}
            federalWithholding={state.federalWithholding}
            onChangeWages={(wages) => setState((s) => ({ ...s, wages }))}
            onChangeWithholding={(federalWithholding) =>
              setState((s) => ({ ...s, federalWithholding }))
            }
          />
        )}
        {step === 3 && (
          <DeductionsStep
            retirement401k={state.retirement401k}
            hsaContribution={state.hsaContribution}
            studentLoanInterest={state.studentLoanInterest}
            onChangeRetirement={(retirement401k) =>
              setState((s) => ({ ...s, retirement401k }))
            }
            onChangeHsa={(hsaContribution) =>
              setState((s) => ({ ...s, hsaContribution }))
            }
            onChangeStudentLoan={(studentLoanInterest) =>
              setState((s) => ({ ...s, studentLoanInterest }))
            }
          />
        )}
        {step === 4 && <ResultStep result={result} onRestart={restart} />}
      </div>

      {step < 4 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={goPrev}
            disabled={step === 0}
            className="rounded-md border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Back
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={!canGoNext}
            className="rounded-md bg-brand-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}